// This file configures the initialization of Sentry on the client.
// The added config here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

;globalThis["_sentryRewritesTunnelPath"] = undefined;globalThis["SENTRY_RELEASE"] = {"id":"08112032be2cd6c51ccbfabedbd7fe523afac014"};globalThis["_sentryBasePath"] = undefined;globalThis["_sentryNextJsVersion"] = "16.1.6";globalThis["_sentryRewriteFramesAssetPrefixPath"] = "";globalThis["_sentryAssetPrefix"] = undefined;globalThis["_sentryExperimentalThirdPartyOriginStackFrames"] = undefined;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/global-management/cost-explorer/:id\",\"regex\":\"^/global-management/cost-explorer/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/databases/:ids\",\"regex\":\"^/databases/([^/]+)$\",\"paramNames\":[\"ids\"],\"hasOptionalPrefix\":false},{\"path\":\"/db/:id\",\"regex\":\"^/db/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/explorer/:resourceId\",\"regex\":\"^/explorer/([^/]+)$\",\"paramNames\":[\"resourceId\"],\"hasOptionalPrefix\":false},{\"path\":\"/jobs/:tab\",\"regex\":\"^/jobs/([^/]+)$\",\"paramNames\":[\"tab\"],\"hasOptionalPrefix\":false},{\"path\":\"/lakehouse/dashboards/:dashboardId/graph/:graphId*?\",\"regex\":\"^/lakehouse/dashboards/([^/]+)/graph(?:/(.*))?$\",\"paramNames\":[\"dashboardId\",\"graphId\"],\"hasOptionalPrefix\":false},{\"path\":\"/lakehouse/notebooks/:notebookId\",\"regex\":\"^/lakehouse/notebooks/([^/]+)$\",\"paramNames\":[\"notebookId\"],\"hasOptionalPrefix\":false},{\"path\":\"/log-space/explore/:id\",\"regex\":\"^/log-space/explore/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/logs/:id\",\"regex\":\"^/logs/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/:not-found*\",\"regex\":\"^/(.+)$\",\"paramNames\":[\"not-found\"],\"hasOptionalPrefix\":false}],\"staticRoutes\":[{\"path\":\"/global-management\"},{\"path\":\"/global-management/api-credentials\"},{\"path\":\"/global-management/audit-logs\"},{\"path\":\"/global-management/azure-tenants\"},{\"path\":\"/global-management/cost-explorer\"},{\"path\":\"/global-management/groups\"},{\"path\":\"/global-management/integrations\"},{\"path\":\"/global-management/notifications\"},{\"path\":\"/global-management/roles\"},{\"path\":\"/global-management/saml\"},{\"path\":\"/global-management/scanning-accounts/aws-accounts\"},{\"path\":\"/global-management/scanning-accounts/azure-subscriptions\"},{\"path\":\"/global-management/scanning-accounts/gcp-projects\"},{\"path\":\"/global-management/users\"},{\"path\":\"/global-management/vault-accounts/aws-accounts\"},{\"path\":\"/global-management/vault-accounts/azure-subscriptions\"},{\"path\":\"/global-management/vault-accounts/gcp-projects\"},{\"path\":\"/onboarding\"},{\"path\":\"/onboarding/in-progress\"},{\"path\":\"/login\"},{\"path\":\"/login-sso\"},{\"path\":\"/logout\"},{\"path\":\"/register\"},{\"path\":\"/assistant/ask\"},{\"path\":\"/assistant/search\"},{\"path\":\"/dashboard\"},{\"path\":\"/inventory\"},{\"path\":\"/lakehouse\"},{\"path\":\"/lakehouse/catalog\"},{\"path\":\"/lakehouse/dashboards\"},{\"path\":\"/lakehouse/ingestion-pipelines\"},{\"path\":\"/lakehouse/job-templates\"},{\"path\":\"/lakehouse/notebooks\"},{\"path\":\"/lakehouse/query-editor\"},{\"path\":\"/lakehouse/query-executions\"},{\"path\":\"/lakehouse/saved-queries\"},{\"path\":\"/lakehouse/spark-engines\"},{\"path\":\"/lakehouse/spark-jobs\"},{\"path\":\"/lakehouse/sql-engines\"},{\"path\":\"/log-space/collect\"},{\"path\":\"/log-space/explore\"},{\"path\":\"/notifications\"},{\"path\":\"/search\"},{\"path\":\"/settings/backup-configuration\"},{\"path\":\"/settings/backup-policies\"},{\"path\":\"/settings/controls\"},{\"path\":\"/settings/object-filters\"},{\"path\":\"/settings/ransomware-detection\"},{\"path\":\"/settings/restore-accounts/aws-accounts\"},{\"path\":\"/settings/restore-accounts/azure-subscriptions\"},{\"path\":\"/settings/restore-accounts/gcp-projects\"},{\"path\":\"/settings/source-accounts/aws-accounts\"},{\"path\":\"/settings/source-accounts/azure-subscriptions\"},{\"path\":\"/settings/source-accounts/discovery-configuration\"},{\"path\":\"/settings/source-accounts/eks-clusters\"},{\"path\":\"/settings/source-accounts/gcp-projects\"},{\"path\":\"/settings/vaults\"},{\"path\":\"/storage-gateway\"},{\"path\":\"/storybook\"},{\"path\":\"/forbidden\"}],\"isrRoutes\":[\"/jobs/:tab\"]}";import type { ApiException } from '@repo/api-gw-sdk';
import {
  init,
  replayIntegration,
  captureRouterTransitionStart,
} from '@sentry/nextjs';

declare global {
  interface Window {
    __RUNTIME_CONFIG__: {
      sentryDSN: string;
      environment: string;
    };
  }
}

const ignoreList = [/\/(envelope|docs|ingest|auth|_next)/, /launchdarkly/];
const env = (window?.__RUNTIME_CONFIG__?.environment || '').toLowerCase();
init({
  enabled:
    ['prod', 'testing', 'demo'].includes(env) &&
    window?.location?.host !== 'eon-monitoring.console.eon.io',

  dsn: window?.__RUNTIME_CONFIG__?.sentryDSN,
  environment: env,

  // Adjust this value in production, or use tracesSampler for greater control
  tracesSampleRate: 1,

  replaysOnErrorSampleRate: 1.0,

  // This sets the sample rate to be 10%. You may want this to be 100% while
  // in development and sample at a lower rate in production
  replaysSessionSampleRate: 0.0,

  sendDefaultPii: true,

  // You can remove this option if you're not planning to use the Sentry Session Replay feature:
  integrations: [
    replayIntegration({
      blockAllMedia: false,
      maskAllInputs: false,
      maskAllText: false,
    }),
  ],

  ignoreSpans: ignoreList,
  ignoreTransactions: ignoreList,

  beforeSend(errorEvent, hint) {
    const originalException = hint.originalException as ApiException<unknown>;
    if (originalException) {
      const code = originalException.code;
      const body = originalException.body as { error: string };

      if (
        code === 404 ||
        (code === 401 && body?.error?.includes('token is expired')) ||
        (originalException instanceof DOMException &&
          originalException.name === 'AbortError')
      ) {
        return null;
      }
    }

    errorEvent.extra = errorEvent.extra || {};
    errorEvent.extra.fullUrl = window.location.href;

    return errorEvent;
  },
});

export const onRouterTransitionStart = captureRouterTransitionStart;

window?.addEventListener('unhandledrejection', function (e) {
  if (
    /ChunkLoadError|Loading chunk [\\s\\S]+ failed/i.test(e?.reason?.message) &&
    performance.now() > 3000
  ) {
    location.reload();
  }
});
